import { Ability, AbilityBuilder } from '@casl/ability';

import { EnvironmentHelper } from '../../../../libs/IdeLink.Web.Common/src/Helpers/EnvironmentHelper';
import { ItemState } from '../ItemState';
import { AdminPermission } from '../Me';
import { ContentPermission } from './ContentPermission';
import { ModulePermissionsConfig, PermissionsLevelThree, PermissionsLevelTwo } from './ModulePermissionsConfig';
import { PermissionType } from './PermissionType';

export interface ToGlobalPermissionsParameters {
    readonly fields?: Array<string>;
    readonly permissionKey: string;
    readonly adminPermissions: Array<AdminPermission>;
    readonly permissionsConfig: ModulePermissionsConfig;
}
export interface ToContentPermissionsParameters {
    readonly permissionKey: string;
    readonly contentState: ItemState;
    readonly contentPermission: ContentPermission;
    readonly permissionsConfig: ModulePermissionsConfig;
}

export class PermissionsAbilityHelper {

    public static defineAdminPermissionsAbility = ({
        fields,
        permissionKey,
        adminPermissions,
        permissionsConfig
    }: ToGlobalPermissionsParameters): Ability => {

        const globalFields = fields || ['create'];
        return AbilityBuilder.define((can: any, cannot: any) => {
            globalFields.forEach((field: string) => {
                PermissionsAbilityHelper.definePermissionsByName(
                    field,
                    permissionKey,
                    permissionsConfig,
                    (permissionsLevelThree: PermissionsLevelThree) => {
                        return (permissionsLevelThree.perms || []).some(
                            (right: PermissionType | string) => {
                                return adminPermissions.some(
                                    (globalPerm: AdminPermission) => globalPerm.permission === right
                                );
                            }
                        );
                    },
                    can
                );
            });
        });
    }

    public static defineContentPermissionsAbility = ({
        contentState,
        permissionKey,
        contentPermission,
        permissionsConfig
    }: ToContentPermissionsParameters): Ability => {
        const permissions: Array<PermissionType> = contentPermission.permissions;

        return AbilityBuilder.define((can: any, cannot: any) => {

            //* levelOneRightName = LEVEL 1 : like create, remove, image, title
            Object.keys(permissionsConfig).forEach((levelOneRightName: string) => {

                //* LEVEL 2 contains right like : can, write, add
                const permissionsLevelTwo: PermissionsLevelTwo = permissionsConfig[levelOneRightName];

                Object.keys(permissionsLevelTwo).forEach((levelTwoRightName: string) => {
                    const permissionsLevelThreeList: Array<PermissionsLevelThree> = permissionsLevelTwo[levelTwoRightName] || [];
                    const hasPermission: boolean = permissionsLevelThreeList.some((permissionsLevelThree: PermissionsLevelThree) => {
                        return PermissionsAbilityHelper.hasPermission(contentState, permissions, permissionsLevelThree);
                    });

                    const permissionName: string = PermissionsAbilityHelper.toPermissionName(levelOneRightName, levelTwoRightName);
                    PermissionsAbilityHelper.logPermission(permissionKey, permissionName, hasPermission)

                    if (hasPermission) {
                        can(permissionName, permissionKey);
                    }
                });
            });
        });
    }

    //#region //* NESTED

    private static definePermissionsByName = (
        permissionField: string, //* LEVEL 1 like create, title, image
        permissionKey: string,
        permissionsConfig: ModulePermissionsConfig,
        hasPermissionForRight: (permissionsLevelThree: PermissionsLevelThree) => boolean,
        can: (name: string, key: string) => any,
        cannot?: any,
    ) => {
        //* LEVEL 2 contains right like : can, write, add
        const permissionsLevelTwo: PermissionsLevelTwo = permissionsConfig[permissionField];

        Object.keys(permissionsLevelTwo).forEach((levelTwoRightName: string) => {
            const permissionsLevelThreeList: Array<PermissionsLevelThree> = permissionsLevelTwo[levelTwoRightName];
            const hasPermission: boolean = permissionsLevelThreeList.some(hasPermissionForRight);
            const permissionName = PermissionsAbilityHelper.toPermissionName(permissionField, levelTwoRightName);
            PermissionsAbilityHelper.logPermission(permissionKey, permissionName, hasPermission)

            if (hasPermission) {
                can(permissionName, permissionKey);
            }
        });
    };

    private static hasPermission = (
        itemState: ItemState,
        permissions: Array<PermissionType>,
        contentPermissionsLevelThree: PermissionsLevelThree) => {
        if (contentPermissionsLevelThree.state === itemState) {
            const perms: Array<PermissionType | string> = contentPermissionsLevelThree.perms || [];

            if (contentPermissionsLevelThree.type === 'all') {
                let atLeastOne = false;
                //* Must have all perms in perms array
                perms.forEach((right: PermissionType | string) => {
                    atLeastOne = !!permissions.find(contentPerm => contentPerm === right);
                });
                return atLeastOne;
            }
            //* Must have at least one perm in perms
            return perms.some((right: PermissionType | string) => permissions.some(contentPerm => contentPerm === right));
        }
        return false;
    }

    private static toPermissionName = (levelOne: string, levelTwo: string): string => {
        return `${levelTwo[0].toUpperCase()}${levelTwo.slice(1)}${levelOne[0].toUpperCase()}${levelOne.slice(1)}`;
    }

    private static logPermission = (key: string, name: string, hasPermission?: boolean) => {
        if (EnvironmentHelper.isDev()) {
            console.debug(
                `%c${key}: %c${name} = %c${hasPermission}`,
                `color: #F5F5F5`,
                `color: #FFD700`,
                `color: ${hasPermission ? '#32CD32' : '#B22222'}`
            );
        }
    }

    //#endregion

    /** //* perms: { [perm: string]: boolean } */
    public static logPermissions = (key: string, perms: any) => {
        if (EnvironmentHelper.isDev()) {
            interface LogPermissionItem {
                name: string;
                hasPermission?: boolean;
                nameColor: string;

                colorPermissionTrue: string;
                backgroundPermissionTrue: string;
                colorPermissionFalse: string;
                backgroundPermissionFalse: string;
            }

            const logWithColors = (key: string, items: Array<LogPermissionItem>) => {

                const withManyLines = items.length > 5;

                const logLineFormat = (item: LogPermissionItem) => {
                    return `%c ${item.name.padEnd(25)}: %c${`${item.hasPermission}`.padEnd(5)}`;
                };
                const logLineColorFormat = (item: LogPermissionItem): Array<string> =>
                    [
                        `background: transparent;
                        color: ${item.hasPermission ? '#FFF' : '#FFF'}`,

                        `background: ${item.hasPermission ? item.backgroundPermissionTrue : item.backgroundPermissionFalse};
                        color: ${item.hasPermission ? item.colorPermissionTrue : item.colorPermissionFalse}`,
                    ];

                const message = `%c${key.padStart(25)}${withManyLines ? '%c\n' : '%c'}${items.map(logLineFormat).join(' ')}`;
                let colorStyles: Array<string> = ['background: #F4A59F; color: #190C0A', 'background: transparent; color: transparent'];
                items.forEach((item) => { colorStyles = [...colorStyles, ...logLineColorFormat(item)] });
				console.log(`${message} %c`, ...colorStyles, 'background: transparent; color: transparent');
            }

            logWithColors(key, Object.keys(perms).map((permission) => ({
                name: permission,
                nameColor: '#FFFFF',
                hasPermission: perms[permission],
                backgroundPermissionTrue: '#4C9915',
                colorPermissionTrue: '#545454',
                backgroundPermissionFalse: '#DF0E09',
                colorPermissionFalse: `#FFFFFF`,
            })));
        }
    }
}